import React, { FC, useState } from "react";
import { Trans } from "@lingui/macro";
import { RemoveScroll } from "react-remove-scroll";
import { LanguageSelector } from "./components/LanguageSelector/LanguageSelector";
import { Logo } from "@silversea-ssc/ui-components-react";
import { INavLinks } from "./type";
import { NavLink } from "./components/NavItem/NavLink";
import classNames from "classnames/bind";
import * as styles from "./HeaderNavBar.module.scss";
import NavSearch from "./components/NavSearch/NavSearch";
import { INavigationLinks } from "src/urls/urls";
import { AlternateLink } from "src/utils/localization/alternateLinks";
import { useMediaQuery } from "react-responsive";
import { Language } from "src/utils/localization/languages/types";
import PhoneNumber from "./components/PhoneNumber/PhoneNumber";
import { legacyUrl } from "src/utils/routing/legacyUrl";

const cx = classNames.bind(styles);

interface NavbarProps {
  links: INavLinks;
}

interface SecondaryNavbarProps extends NavbarProps {
  alternateLinks: AlternateLink[];
  language: Language;
  searchUrl?: string;
}

const SecondaryNavbar: FC<SecondaryNavbarProps> = ({
  links,
  alternateLinks,
  language,
  searchUrl
}) => (
  <nav
    className={styles.secondaryNavbar}
    aria-label="Secondary menu"
    data-content-piece="secondary_menu"
  >
    <ul>
      {links.map((l) => (
        <li key={l.href}>
          <NavLink variant="secondary" link={l} expandOnMobile={true} />
        </li>
      ))}

      <li>
        <LanguageSelector
          currentLanguage={language}
          alternateLinks={alternateLinks}
        />
      </li>

      {searchUrl && (
        <li>
          <NavSearch searchUrl={searchUrl} />
        </li>
      )}
    </ul>
  </nav>
);

const PrimaryNavbar: FC<NavbarProps> = ({ links }) => {
  return (
    <nav
      className={styles.primaryNavbar}
      aria-label="Navigation menu"
      data-content-piece="navigation_menu"
    >
      <ul>
        {links.map((l) => (
          <li key={l.href}>
            <NavLink variant="primary" link={l} expandOnMobile={true} />
          </li>
        ))}
      </ul>
    </nav>
  );
};

const ServiceNavbar = ({ children }) => {
  return (
    <nav
      className={styles.serviceNavbar}
      aria-label="Service menu"
      data-content-piece="service_menu"
    >
      <ul>
        {children.map((c, index) => (
          <li key={index}>{c}</li>
        ))}
      </ul>
    </nav>
  );
};

interface HamburgeButtonProps {
  onToggleMenu: () => void;
  isOpen: boolean;
}

const HamburgeButton: FC<HamburgeButtonProps> = ({ isOpen, onToggleMenu }) => {
  const label = isOpen ? "Close secondary menu" : "Open secondary menu";

  return (
    <React.Fragment>
      <button
        className={cx({
          menuButton: true,
          menuButtonOpened: isOpen
        })}
        aria-controls="ssc-secondary-menu-content"
        aria-expanded={isOpen}
        aria-label={label}
        onClick={onToggleMenu}
      >
        <label className={styles.menuIcon} htmlFor="menu-btn">
          <span className={styles.menuLabel}>
            <Trans>MENU</Trans>
          </span>
          <span className={styles.navicon} aria-hidden="true"></span>
        </label>
      </button>
    </React.Fragment>
  );
};

export interface HeaderNavBarProps {
  alternateLinks?: AlternateLink[];
  primaryLinks?: INavLinks;
  secondaryLinks?: INavLinks;
  serviceLinks?: INavLinks;
  searchUrl?: string;
  language: Language;
}

export const HeaderNavBar: FC<HeaderNavBarProps> = ({
  primaryLinks,
  secondaryLinks,
  serviceLinks,
  alternateLinks,
  searchUrl,
  language
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuIsDesktop = useMediaQuery({ query: "(min-width: 1024px)" });

  const menuItems = (
    <React.Fragment>
      <SecondaryNavbar
        links={secondaryLinks || []}
        alternateLinks={alternateLinks || []}
        language={language}
        searchUrl={searchUrl}
      />
      <PrimaryNavbar links={primaryLinks || []} />
    </React.Fragment>
  );

  return (
    <nav className={styles.header}>
      <div className={styles.headerContainer}>
        <div className={styles.brandLogo}>
          <a href={legacyUrl(`/${language.path}`)} style={{ display: "block" }}>
            <Logo />
          </a>
        </div>
        <ServiceNavbar>
          <PhoneNumber />
          {serviceLinks &&
            serviceLinks.map((l) => (
              <NavLink
                key={l.href}
                link={l}
                variant="secondary"
                expandOnMobile={false}
                type="button"
              />
            ))}
        </ServiceNavbar>

        {menuIsDesktop ? (
          menuItems
        ) : (
          <RemoveScroll enabled={isMenuOpen} forwardProps>
            <div
              id="ssc-secondary-menu-content"
              className={cx({
                navbarsContainer: true,
                navbarsContainerExpanded: isMenuOpen,
                scroll: true
              })}
            >
              {menuItems}
            </div>
          </RemoveScroll>
        )}

        <HamburgeButton
          isOpen={isMenuOpen}
          onToggleMenu={() => setIsMenuOpen(!isMenuOpen)}
        />
      </div>
    </nav>
  );
};

export interface StaticHeaderNavBarProps {
  alternateLinks?: AlternateLink[];
  navigationLinks: INavigationLinks;
  language: Language;
  searchUrl: string;
  primaryLinks: INavLinks;
  secondaryLinks: INavLinks;
}

export const StaticHeaderNavBar: React.FC<StaticHeaderNavBarProps> = ({
  language,
  navigationLinks,
  alternateLinks,
  searchUrl,
  primaryLinks,
  secondaryLinks
}) => {
  const enhancedSecondaryLinks = secondaryLinks.map((l) =>
    l.id === "brochures"
      ? { ...l, ["data-content-piece"]: "view_the_brochures" }
      : l
  );
  const serviceLinks = [
    {
      ...navigationLinks.serviceLinks.raq,
      id: "raq",
      ["data-content-piece"]: "request_a_quote"
    }
  ];

  return (
    <HeaderNavBar
      alternateLinks={alternateLinks}
      secondaryLinks={enhancedSecondaryLinks}
      primaryLinks={primaryLinks}
      serviceLinks={serviceLinks}
      searchUrl={searchUrl}
      language={language}
    />
  );
};
